import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ManualLabourAssignmentModal from "../../../components/forms/orders/ManualLabourAssignmentModal";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";

export function ManualLaboursAssignedViewModel(logout, orderId, snackbar) {
  const [loading, setLoading] = useState(false);
  const [manualLaboursAssigned, setManualLaboursAssigned] = useState([]);
  const [manualLabourOptions, setManualLabourOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const getManualLaboursAssignedByOrderId = async function (showLoading = true) {
    if (showLoading) setLoading(true);

    let callResult = await makeGetApiCall(
      `manualLaboursAssigned/order/${orderId}`,
    );
    if (showLoading) setLoading(false);
    if (callResult.success) {
      setManualLaboursAssigned(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManualLabourAssignedById = async function (id) {
    setLoading(true);

    let callResult = await makeGetApiCall(`manualLaboursAssigned/${id}`);
    setLoading(false);
    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      return null;
    }
  };

  const getManualLaboursForDropdown = async function () {
    let callResult = await makeGetApiCall("manualLaboursForDropdown");
    if (callResult.success) {
      setManualLabourOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const createManualLabourAssigned = async function (values) {
    const payload = { ...values };
    let callResult = await makePostApiCall(
      "manualLaboursAssigned/create",
      payload,
    );

    if (callResult.success) {
      snackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Manual Labour Assignment added",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getManualLaboursAssignedByOrderId();
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateManualLabourAssigned = async function (values, index) {
    let callResult = await makePutApiCall(
      `manualLaboursAssigned/${values.id}`,
      values,
    );

    if (callResult.success) {
      snackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Manual Labour Assignment updated",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });

      await getManualLaboursAssignedByOrderId(false);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }

    return callResult;
  };

  const openAddManualLabourAssignedModal = (isEdit, item, index) => {
    setEmptyModelOpen({
      childComponent: (
        <ManualLabourAssignmentModal
          item={item}
          isEdit={isEdit}
          setOpen={setEmptyModelOpen}
          actionCall={(payload) =>
            isEdit
              ? updateManualLabourAssigned(payload, index)
              : createManualLabourAssigned(payload)
          }
          title={
            isEdit
              ? `Update Assignment - ${item.name || "N/A"}`
              : "Add new manual labour assignment"
          }
          manualLabourOptions={manualLabourOptions}
          employeeOptions={employeeOptions}
          orderId={orderId}
        />
      ),
      open: true,
    });
  };

  const deleteManualLabourAssigned = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteManualLabourAssigned/${id}`);
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const newItems = manualLaboursAssigned.items.filter(
        (item) => item.id !== id,
      );
      setManualLaboursAssigned({ ...manualLaboursAssigned, items: newItems });

      snackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const askDeleteManualLabourAssigned = function (item, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteManualLabourAssigned(item.id, index)}
          message={`Are you sure you want to delete this manual labour assignment${
            item.name ? ` for ${item.name}` : ""
          }?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  const getEmployeeOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`usersForDropdown`);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  return {
    openAddManualLabourAssignedModal,
    updateManualLabourAssigned,
    getManualLaboursAssignedByOrderId,
    getManualLabourAssignedById,
    getManualLaboursForDropdown,
    loading,
    manualLaboursAssigned,
    manualLabourOptions,
    setEmptyModelOpen,
    emptyModal,
    askDeleteManualLabourAssigned,
    getEmployeeOptionsForDropdown,
    employeeOptions,
  };
}
